import { memo } from 'react';
import { connect, useSelector } from 'react-redux';

import { PM_WARNING } from 'CorePackage/MainModule/routes/routes';
import {
  NOTE_WARNING_BLOCK_ID,
  NOTE_WARNING_REACT_QUILL_EDITOR_BLOCK_ID,
} from 'KnmPmPackage/MainModule/constants/knmPmFieldsElementsIds';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { KnmVersionsEnum } from 'APIPackage/KnmModule/enums/KnmVersionsEnum';

import { PmContentWarning } from './PmContentWarning';
import { PmObjectionWarning } from './PmObjectionWarning';
import styles from './PmWarning.module.scss';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IKnmFormRef } from '../../../KnmPackage/MainModule/interfaces/IKnmFormRef';
import { ReactQuillEditorField } from '../../../UIPackage/MainModule/components/ReactQuillEditorField';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { getKnmData, getKnmDataVersion } from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { IKnm } from '../../../APIPackage/KnmModule/interfaces/IKnm';
import { ProfKindCodesEnum } from '../../../APIPackage/DictionariesModule/enums/ProfKindCodesEnum';
import { ExternalSystemSignInfoBlock } from '../../../CorePackage/MainModule/components/ExternalSystemSignInfoBlock';
import { PmExampleWarning } from './PmExampleWarning';

interface IPmWarningProps {
  className?: string;
  contentWarningAttachmentsNumber: number;
  created: string | undefined;
  formRef: IKnmFormRef | undefined;
  isWatchOnly: boolean;
  objectionWarningAttachmentsNumber: number;
  exampleWarningAttachmentsNumber: number;
}

interface IStoreProps {
  knmRawData: IKnm | undefined;
  isErp: boolean;
}

export const PmWarningWithoutStore = memo(
  ({
    className = '',
    contentWarningAttachmentsNumber,
    created,
    formRef,
    isWatchOnly,
    objectionWarningAttachmentsNumber,
    knmRawData,
    exampleWarningAttachmentsNumber,
    isErp,
  }: IPmWarningProps & IStoreProps) => {
    const settings = useSelector(getAdminFetchSettings);

    const isValid4121Cert = isLaterThanAdminSetting(
      created,
      settings,
      AdminSettingCodesEnum.KNM_VALIDATION_4_1_21_CERT_STARTS_FROM,
    );

    const pmType = formRef?.current.getFieldState('pmType')?.value;
    const isTypeOP = pmType?.code === ProfKindCodesEnum.OP;

    return (
      <div className={className ? `${styles.Wrapper} ${className}` : styles.Wrapper} id={PM_WARNING}>
        <h2 className="shared-title__with-margin">Сведения о содержании предостережения</h2>

        <div className={styles.NoteWarningFieldBlock} id={NOTE_WARNING_BLOCK_ID}>
          {isValid4121Cert && (
            <>
              {isTypeOP && knmRawData?.pmErknm?.warningIssuingSystemSignatureUsed ? (
                <ExternalSystemSignInfoBlock
                  systemSignatureDateTime={knmRawData.pmErknm.warningIssuingSystemSignatureDateTime as string}
                  systemSignatureTitle={knmRawData.pmErknm.warningIssuingSystemSignatureTitle as string}
                />
              ) : (
                <>
                  <Row>
                    <FieldBlock label="ФИО должностного лица" color="black" size="half">
                      <TextareaField id="officialFullName" name="officialFullName" disabled={true} />
                    </FieldBlock>
                    <FieldBlock
                      label="Должность лица, сформировавшего содержание предостережения"
                      color="black"
                      size="half"
                    >
                      <TextareaField
                        id="warningContentCreatorPosition"
                        name="warningContentCreatorPosition"
                        disabled={true}
                      />
                    </FieldBlock>
                  </Row>
                </>
              )}
            </>
          )}
          <div className="shared-group-margin" id={NOTE_WARNING_REACT_QUILL_EDITOR_BLOCK_ID}>
            <label className="shared-group-label" htmlFor="noteWarningReactQuillEditor">
              {getTitleFieldWithRedStar({
                label: 'Описание предостережения',
                field: 'noteWarningReactQuillEditor',
                sectionName: 'noteWarningReactQuillEditor',
                isPm: true,
              })}
            </label>
          </div>
          <ReactQuillEditorField
            id="noteWarningReactQuillEditor"
            name="noteWarningReactQuillEditor"
            placeholder="Введите описание"
            disabled={isWatchOnly}
            handleCustomChange={(e: string) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              formRef?.current?.change(`noteWarning` as any, e);
            }}
          />
        </div>
        {isErp ? (
          <PmExampleWarning
            className={styles.ObjectionWarning}
            isWatchOnly={isWatchOnly}
            attachmentsNumber={exampleWarningAttachmentsNumber}
            formRef={formRef}
          />
        ) : (
          <PmContentWarning
            className={styles.ContentWarning}
            watchOnly={isWatchOnly}
            attachmentsNumber={contentWarningAttachmentsNumber}
          />
        )}

        <PmObjectionWarning
          className={styles.ObjectionWarning}
          isWatchOnly={isWatchOnly}
          attachmentsNumber={objectionWarningAttachmentsNumber}
          formRef={formRef}
        />
      </div>
    );
  },
);

const mapStateToProps = (state: IRootState): IStoreProps => ({
  knmRawData: getKnmData(state),
  isErp: getKnmDataVersion(state) === KnmVersionsEnum.ERP,
});

export const PmWarning = connect(mapStateToProps)(PmWarningWithoutStore);
